<template>
  <v-row class="background">
    <v-col cols="10">
      <chart-by-month
        :resultSetByMonth="resultSetByMonth"
        :chartDataMonth="chartDataMonthECS"
        :chartDataAverage="chartDataAverageECS"
        :graph="graph"
        :displayed="displayed"
      />
    </v-col>
    <v-col cols="2">
      <v-simple-table class="elevation-2 secondarybackground mt-3">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">
                {{ generateMessage('properties_360.readings.annee') }}
              </th>
              <th class="text-left">
                {{ generateMessage('properties_360.readings.qmoyen') }}
              </th>
              <th class="text-left">
                {{ generateMessage('properties_360.readings.m3j') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in ecsArray" :key="item.annee">
              <td>{{ item.annee }}</td>
              <td>
                {{ item.averageq ? Number(item.averageq).toFixed(3) : '' }}
              </td>
              <td>
                <v-tooltip
                  top
                  v-if="
                    ecsArrayEcHc[index] &&
                      ecsArrayEcHc[index].annee === item.annee
                  "
                >
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      {{
                        item.dailyCubeMeters
                          ? item.dailyCubeMeters.toFixed(2)
                          : ''
                      }}
                    </div>
                  </template>
                  <span>
                    EC :
                    {{ ecsArrayEcHc[index].averagM3Ec }}- HC :
                    {{ ecsArrayEcHc[index].averagM3Hc }}
                  </span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-col>
  </v-row>
</template>

<script>
import { generateMessage } from '@/utils/i18n';
import ChartByMonth from './ChartByMonth';
import { resultList, monthLabels, showColor } from './ReadingsUtils';

export default {
  name: 'ReadingsECSByMonth',
  components: {
    ChartByMonth,
  },
  props: {
    resultSetByMonth: { type: Array, required: false },
    graph: { type: Number, required: false, default: 2 },
    resultSet: { type: Array, required: false },
    resultSetCumulated: { type: Array, required: false },
    displayed: { type: Boolean, required: false },
  },
  methods: {
    generateMessage,
    showColor,
    chartECSJ(index, color) {
      return {
        type: 'line',
        label: `${this.generateMessage('properties_360.readings.ecsj')} ${
          this.yearsMonth[index]
        }`,
        data: this.labels.map((element) => {
          const val = this.yearsMonthData[index].find(
            (elem) => elem.mois === element
          );
          return val && val.ecs_j ? val.ecs_j.toFixed(2) : null;
        }),
        _short: true,
        borderColor: color,
        backgroundColor: 'transparent',
        yAxisID: 'yLeft2',
      };
    },
    chartQR(index, color) {
      const labqr = resultList.find((el) => el.value === 'q_ecs_hc').text;
      return {
        label: `${labqr} ${this.yearsMonth[index]}`,
        data: this.labels.map((element) => {
          const val = this.yearsMonthData[index].find(
            (elem) => elem.mois === element
          );
          if (val) {
            return val.q_ecs_hc;
          }
          return null;
        }),
        _short: true,
        borderColor: color,
        borderWidth: 2,
        yAxisID: 'yRight2',
      };
    },
    getSum(array) {
      return array
        .map((el) => ({
          conso: el.ecs_j * el.nbjour,
          nbjour: el.nbjour,
        }))
        .reduce(
          (a, b) => ({
            conso: a.conso + b.conso,
            nbjour: a.nbjour + b.nbjour,
          }),
          {
            conso: 0,
            nbjour: 0,
          }
        );
    },
  },
  computed: {
    yearsMonth() {
      return this.resultSetByMonth[0] && this.resultSetByMonth[0].an
        ? [
            this.resultSetByMonth[0].an,
            this.resultSetByMonth[0].an + 1,
            this.resultSetByMonth[0].an + 2,
          ]
        : [];
    },
    labels() {
      return monthLabels.map((el) => el.id);
    },
    yearsMonthData() {
      return this.yearsMonth.map((year) =>
        this.resultSetByMonth.filter((item) => item.an === year)
      );
    },

    chartDataMonthECS() {
      // Transform data for visualization
      if (this.yearsMonth.length) {
        const items = this.resultSetByMonth;

        const datasets = [];

        datasets.push(this.chartECSJ(0, 'rgba(75, 192, 192, 1)'));
        datasets.push(this.chartECSJ(1, 'rgba(145, 56, 0, 1)'));
        datasets.push(this.chartECSJ(2, 'rgba(200,200,50,1)'));

        datasets.push(this.chartQR(0, 'rgba(75, 192, 192, 1)'));
        datasets.push(this.chartQR(1, 'rgba(145, 56, 0, 1)'));
        datasets.push(this.chartQR(2, 'rgba(200,200,50,1)'));

        return {
          labels: this.labels,
          items,
          datasets,
        };
      }
      return {
        labels: [],
        items: [],
        datasets: [],
      };
    },

    chartDataAverageECS() {
      // Transform data for visualization
      if (this.yearsMonth.length) {
        const items = this.resultSetByMonth;
        const labels = ['A-2', 'A-1', 'En cours', 'Moyenne'];
        const datasets = [];
        const labECS = resultList.find((el) => el.value === 'ecs_j').text;
        const averageCurrent = this.getSum(this.yearsMonthData[2]);
        const averageMinus1 = this.getSum(this.yearsMonthData[1]);
        const averageMinus2 = this.getSum(this.yearsMonthData[0]);
        if (averageMinus2.conso) {
          datasets.push({
            label: `${labECS} A-2`,
            data: [
              averageMinus2.nbjour
                ? (averageMinus2.conso / averageMinus2.nbjour).toFixed(3)
                : null,
              null,
              null,
            ],
            _short: true,
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 2,
            yAxisID: 'yLeft1',
          });
        }
        if (averageMinus1.conso) {
          datasets.push({
            label: `${labECS} A-1`,
            data: [
              null,
              averageMinus1.nbjour
                ? (averageMinus1.conso / averageMinus1.nbjour).toFixed(3)
                : null,
            ],
            _short: true,
            borderColor: 'rgba(145, 56, 0, 1)',
            borderWidth: 2,
            yAxisID: 'yLeft1',
          });
        }
        datasets.push({
          label: `${labECS} En cours`,
          data: [
            null,
            null,
            averageCurrent.nbjour
              ? (averageCurrent.conso / averageCurrent.nbjour).toFixed(3)
              : null,
          ],
          _short: true,
          borderColor: 'rgba(200, 200, 50, 1)',
          borderWidth: 2,
          yAxisID: 'yLeft1',
        });

        datasets.push({
          label: `${labECS} Moyen`,
          data: [
            null,
            null,
            null,
            averageCurrent.nbjour
              ? (
                  (averageCurrent.conso +
                    averageMinus1.conso +
                    averageMinus2.conso) /
                  (averageCurrent.nbjour +
                    averageMinus1.nbjour +
                    averageMinus2.nbjour)
                ).toFixed(3)
              : null,
          ],
          _short: true,
          borderColor: 'rgba(192, 192, 192, 1)',
          borderWidth: 2,
          yAxisID: 'yLeft1',
        });
        return {
          labels,
          items,
          datasets,
        };
      }
      return {
        labels: [],
        items: [],
        datasets: [],
      };
    },
    ecsArray() {
      if (this.resultSetCumulated && this.resultSetCumulated[0]) {
        const res0 = this.resultSetCumulated
          .filter((item) => item.an === this.yearsMonth[0])
          .pop();
        const res1 = this.resultSetCumulated
          .filter((item) => item.an === this.yearsMonth[1])
          .pop();
        const res2 = this.resultSetCumulated
          .filter((item) => item.an === this.yearsMonth[2])
          .pop();
        const retour = [];
        if (res0) {
          retour.push({
            annee: res0.an,
            averageq: res0.qreel_ener || res0.qreel_comb,
            dailyCubeMeters: res0.ecs_j,
          });
          if (res1) {
            retour.push({
              annee: res1.an,
              averageq: res1.qreel_ener || res1.qreel_comb,
              dailyCubeMeters: res1.ecs_j,
            });
            if (res2) {
              retour.push({
                annee: res2.an,
                averageq: res2.qreel_ener || res2.qreel_comb,
                dailyCubeMeters: res2.ecs_j,
              });
            }
          }
        }
        return retour;
      }
      return [];
    },
    ecsArrayEcHc() {
      if (this.resultSet && this.resultSet[0]) {
        const res0 = this.resultSet.filter(
          (item) => item.an === this.yearsMonth[0]
        );
        const res1 = this.resultSet.filter(
          (item) => item.an === this.yearsMonth[1]
        );
        const res2 = this.resultSet.filter(
          (item) => item.an === this.yearsMonth[2]
        );
        const retour = [];
        if (res0 && res0[0] && res0[0].an) {
          const ec = res0
            .filter((elem) => elem.nbjour_chauffe > 0)
            .reduce(
              (a, b) => ({
                m3: a.m3 + b.ecs_m3,
                nbj: a.nbj + b.nbjour_chauffe,
              }),
              {
                m3: 0,
                nbj: 0,
              }
            );
          const hc = res0
            .filter((elem) => elem.nbjour_chauffe === 0)
            .reduce(
              (a, b) => ({
                m3: a.m3 + b.ecs_m3,
                nbj: a.nbj + b.nbjour,
              }),
              {
                m3: 0,
                nbj: 0,
              }
            );
          retour.push({
            annee: res0[0].an,
            averagM3Ec: ec.nbj > 0 ? (ec.m3 / ec.nbj).toFixed(2) : 0,
            averagM3Hc: hc.nbj > 0 ? (hc.m3 / hc.nbj).toFixed(2) : 0,
          });
          if (res1 && res1[0] && res1[0].an) {
            const ec1 = res1
              .filter((elem) => elem.nbjour_chauffe > 0)
              .reduce(
                (a, b) => ({
                  m3: a.m3 + b.ecs_m3,
                  nbj: a.nbj + b.nbjour_chauffe,
                }),
                {
                  m3: 0,
                  nbj: 0,
                }
              );
            const hc1 = res1
              .filter((elem) => elem.nbjour_chauffe === 0)
              .reduce(
                (a, b) => ({ m3: a.m3 + b.ecs_m3, nbj: a.nbj + b.nbjour }),
                {
                  m3: 0,
                  nbj: 0,
                }
              );
            retour.push({
              annee: res1[0].an,
              averagM3Ec: ec1.nbj > 0 ? (ec1.m3 / ec1.nbj).toFixed(2) : 0,
              averagM3Hc: hc1.nbj > 0 ? (hc1.m3 / hc1.nbj).toFixed(2) : 0,
            });
            if (res2 && res2[0] && res2[0].an) {
              const ec2 = res2
                .filter((elem) => elem.nbjour_chauffe > 0)
                .reduce(
                  (a, b) => ({
                    m3: a.m3 + b.ecs_m3,
                    nbj: a.nbj + b.nbjour_chauffe,
                  }),
                  {
                    m3: 0,
                    nbj: 0,
                  }
                );
              const hc2 = res2
                .filter((elem) => elem.nbjour_chauffe === 0)
                .reduce(
                  (a, b) => ({
                    m3: a.m3 + b.ecs_m3,
                    nbj: a.nbj + b.nbjour,
                  }),
                  {
                    m3: 0,
                    nbj: 0,
                  }
                );
              retour.push({
                annee: res2[0].an,
                averagM3Ec: ec2.nbj > 0 ? (ec2.m3 / ec2.nbj).toFixed(2) : 0,
                averagM3Hc: hc2.nbj > 0 ? (hc2.m3 / hc2.nbj).toFixed(2) : 0,
              });
            }
          }
        }
        return retour;
      }
      return [];
    },
  },
};
</script>
